import React, { useEffect, useMemo, useState } from "react";
import { Menu } from "@mantine/core";
import {
  getReportPreview,
  listCannedReports,
  upsertCannedReportAssignments,
  deleteCannedReportAssignment,
  type AdministrationContext,
  type AdministrationRole,
  type AdministrationTenant,
  type CannedReport,
  type CannedReportAssignment,
  type ReportPreview,
} from "../services/reportAdministration.ts";

type Props = {
  accessToken: string;
  email?: string | null;
  context: AdministrationContext | null;
  contextError?: string | null;
  onPreview: (preview: ReportPreview) => void;
  onCreateReport: (sourceMode?: "model" | "sql") => void;
  onCreateDashboard: () => void;
};

export default function ReportAdministration({
  accessToken,
  email,
  context,
  contextError,
  onPreview,
  onCreateReport,
  onCreateDashboard,
}: Props) {
  const tenants = useMemo(() => context?.tenants || [], [context]);
  const [tenantId, setTenantId] = useState<string>("");
  const [roleId, setRoleId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<CannedReport[]>([]);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string>("");
  const [assignmentTenantId, setAssignmentTenantId] = useState<string>("");
  const [assignmentRoleId, setAssignmentRoleId] = useState<string>("");
  const [assignmentCanEdit, setAssignmentCanEdit] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState<CannedReportAssignment[]>([]);
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  useEffect(() => {
    if (!tenantId && tenants.length) setTenantId(String(tenants[0].id));
  }, [tenantId, tenants]);

  const selectedTenant = useMemo<AdministrationTenant | undefined>(
    () => tenants.find((tenant) => String(tenant.id) === tenantId),
    [tenantId, tenants],
  );
  const roles = useMemo(() => selectedTenant?.roles || [], [selectedTenant]);

  useEffect(() => {
    if (!roles.length) {
      setRoleId("");
      return;
    }
    if (!roles.some((role) => String(role.id) === roleId)) {
      setRoleId(String(roles[0].id));
    }
  }, [roleId, roles]);

  const loadReports = async () => {
    try {
      const result = await listCannedReports(accessToken, email || undefined);
      setReports(result.reports || []);
      setReportsError(null);
    } catch (cause: any) {
      setReportsError(cause?.message || "Unable to load the canned report catalog.");
    }
  };

  useEffect(() => {
    if (context?.is_company_administrator) void loadReports();
  // The catalog should reload when the authenticated administrator changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, email, context?.is_company_administrator]);

  useEffect(() => {
    if (!selectedReportId && reports.length) setSelectedReportId(String(reports[0].id));
  }, [reports, selectedReportId]);

  useEffect(() => {
    if (!assignmentTenantId && tenants.length) setAssignmentTenantId(String(tenants[0].id));
  }, [assignmentTenantId, tenants]);

  const assignmentTenant = useMemo(
    () => tenants.find((tenant) => String(tenant.id) === assignmentTenantId),
    [assignmentTenantId, tenants],
  );
  const assignmentRoles = useMemo(
    () => assignmentTenant?.roles || [],
    [assignmentTenant],
  );

  useEffect(() => {
    if (assignmentRoles.length && !assignmentRoles.some((role) => String(role.id) === assignmentRoleId)) {
      setAssignmentRoleId(String(assignmentRoles[0].id));
    }
  }, [assignmentRoleId, assignmentRoles]);

  const selectedReport = reports.find((report) => String(report.id) === selectedReportId);
  const canEditIsApplicable = !selectedReport?.has_saved_item;

  useEffect(() => {
    if (!canEditIsApplicable) setAssignmentCanEdit(false);
  }, [canEditIsApplicable]);

  const addAssignment = () => {
    if (!assignmentTenant || !assignmentRoleId) return;
    const next: CannedReportAssignment = {
      tenant_id: assignmentTenant.id,
      tenant_code: assignmentTenant.code,
      role_id: Number(assignmentRoleId),
      role_code: assignmentTenant.roles.find((role) => String(role.id) === assignmentRoleId)?.code,
      can_view: true,
      can_edit: canEditIsApplicable && assignmentCanEdit,
      can_schedule: false,
    };
    setPendingAssignments((current) =>
      current.some((assignment) => assignment.tenant_id === next.tenant_id && assignment.role_id === next.role_id)
        ? current
        : [...current, next],
    );
  };

  const saveAssignments = async () => {
    if (!selectedReport || !pendingAssignments.length) return;
    setAssignmentSaving(true);
    setReportsError(null);
    try {
      await upsertCannedReportAssignments(accessToken, selectedReport.id, pendingAssignments, email || undefined);
      setPendingAssignments([]);
      await loadReports();
    } catch (cause: any) {
      setReportsError(cause?.message || "Unable to save report assignments.");
    } finally {
      setAssignmentSaving(false);
    }
  };

  const removeAssignment = async (assignment: CannedReportAssignment) => {
    if (!selectedReport || !window.confirm(`Remove ${assignment.tenant_code || assignment.tenant_id} / ${assignment.role_code || assignment.role_id}?`)) return;
    setAssignmentSaving(true);
    try {
      await deleteCannedReportAssignment(accessToken, selectedReport.id, assignment.tenant_id, assignment.role_id, email || undefined);
      await loadReports();
    } catch (cause: any) {
      setReportsError(cause?.message || "Unable to remove report assignment.");
    } finally {
      setAssignmentSaving(false);
    }
  };

  const preview = async () => {
    if (!selectedTenant || !roleId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getReportPreview(
        accessToken,
        selectedTenant.id,
        Number(roleId),
        email || undefined,
      );
      onPreview(result);
    } catch (cause: any) {
      setError(cause?.message || "Unable to load the preview.");
    } finally {
      setLoading(false);
    }
  };

  if (contextError) {
    return (
      <div className="rt-admin-page">
        <h2>Report Administration</h2>
        <p className="rt-admin-error">{contextError}</p>
      </div>
    );
  }

  return (
    <div className="rt-admin-page">
      <div className="rt-admin-heading">
        <div>
          <p className="rt-admin-eyebrow">Company administration</p>
          <h2>Report Administration</h2>
          <p>
            Preview the portal for a tenant and role before managing its report
            assignments.
          </p>
        </div>
      </div>

      <section className="rt-admin-card" aria-labelledby="preview-title">
        <h3 id="preview-title">Preview portal menu</h3>
        <p className="rt-admin-card-description">
          This is read-only. It uses the same access rules that build the
          selected tenant-role menu.
        </p>

        <div className="rt-admin-form-row">
          <label>
            Tenant
            <select
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
              disabled={!tenants.length || loading}
            >
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.code} — {tenant.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Role
            <select
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              disabled={!roles.length || loading}
            >
              {roles.map((role: AdministrationRole) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>

          <button
            className="rt-admin-primary"
            type="button"
            onClick={preview}
            disabled={!selectedTenant || !roleId || loading}
          >
            {loading ? "Loading preview…" : "Preview portal"}
          </button>
        </div>
        {error && <p className="rt-admin-error" role="alert">{error}</p>}
      </section>

      <section className="rt-admin-card rt-admin-workspace">
        <div>
          <h3>Manage reports</h3>
          <p className="rt-admin-card-description">
            Create a report or dashboard, then assign it to one or more
            tenant-role combinations from the assignment workspace.
          </p>
        </div>
        <div className="rt-admin-actions">
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <button type="button" className="rt-admin-secondary">
                Create report
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => onCreateReport("model")}>From Model</Menu.Item>
              <Menu.Item onClick={() => onCreateReport("sql")}>From SQL</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          <button type="button" className="rt-admin-secondary" onClick={onCreateDashboard}>
            Create dashboard
          </button>
        </div>
      </section>

      <section className="rt-admin-card" aria-labelledby="assignment-title">
        <h3 id="assignment-title">Assign canned reports</h3>
        <p className="rt-admin-card-description">
          Choose an existing report, add as many tenant-role targets as needed, then save them together.
        </p>
        <div className="rt-admin-form-row">
          <label className="rt-admin-report-picker">
            Canned report
            <select value={selectedReportId} onChange={(event) => setSelectedReportId(event.target.value)} disabled={!reports.length || assignmentSaving}>
              {reports.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.title} — {report.mb_resource_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tenant
            <select value={assignmentTenantId} onChange={(event) => setAssignmentTenantId(event.target.value)} disabled={assignmentSaving}>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.code} — {tenant.name}</option>)}
            </select>
          </label>
          <label>
            Role
            <select value={assignmentRoleId} onChange={(event) => setAssignmentRoleId(event.target.value)} disabled={!assignmentRoles.length || assignmentSaving}>
              {assignmentRoles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
            </select>
          </label>
          <label className="rt-admin-can-edit">
            <input
              type="checkbox"
              checked={assignmentCanEdit}
              disabled={!canEditIsApplicable || assignmentSaving}
              onChange={(event) => setAssignmentCanEdit(event.currentTarget.checked)}
            />
            Can edit
          </label>
          <button type="button" className="rt-admin-secondary" onClick={addAssignment} disabled={!selectedReport || !assignmentRoleId || assignmentSaving}>
            Add target
          </button>
        </div>
        {selectedReport && !canEditIsApplicable && (
          <p className="rt-admin-card-description">
            This report was published from a user&apos;s saved item, so editing is
            controlled by the creator or a Report Curator/Admin, not by the
            &quot;Can edit&quot; toggle here.
          </p>
        )}

        {pendingAssignments.length > 0 && (
          <div className="rt-admin-pending">
            <strong>Ready to assign</strong>
            {pendingAssignments.map((assignment) => (
              <span key={`${assignment.tenant_id}-${assignment.role_id}`} className="rt-admin-chip">
                {assignment.tenant_code} · {assignment.role_code}{assignment.can_edit ? " · editable" : ""}
                <button type="button" onClick={() => setPendingAssignments((current) => current.filter((item) => item !== assignment))} aria-label="Remove pending assignment">×</button>
              </span>
            ))}
            <button type="button" className="rt-admin-primary" onClick={saveAssignments} disabled={assignmentSaving}>
              {assignmentSaving ? "Saving…" : "Save assignments"}
            </button>
          </div>
        )}

        {selectedReport && (
          <div className="rt-admin-existing">
            <strong>Current assignments</strong>
            {selectedReport.assignments?.length ? selectedReport.assignments.map((assignment) => (
              <span key={`${assignment.tenant_id}-${assignment.role_id}`} className="rt-admin-chip">
                {assignment.tenant_code} · {assignment.role_code}{assignment.can_edit ? " · editable" : ""}
                <button type="button" disabled={assignmentSaving} onClick={() => removeAssignment(assignment)} aria-label="Remove assignment">×</button>
              </span>
            )) : <span className="rt-admin-empty">No tenant-role assignments yet.</span>}
          </div>
        )}
        {reportsError && <p className="rt-admin-error" role="alert">{reportsError}</p>}
      </section>
    </div>
  );
}
